import axios, {
  AxiosError,
  AxiosHeaderValue,
  AxiosRequestHeaders,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

import { dialogService } from '@thefarmersfront/kitchen-mobile';

import { networkConfig } from '~/configs/networkConfig';
import { API_BASE_URL, API_VERSION, DIALOG_MESSAGES, DIALOG_TITLES, REQUEST_TIMEOUT } from '~/constants';
import {
  useCheckPasswordStore,
  useLoadingStore,
  useShouldCheckModalStore,
  useSkipFeaturesStore,
  useTokenStore,
  useUserInfoStore,
  useWorkInfoStore,
} from '~/store';
import { useContractInputStore, useTabInfoStore } from '~/store/contractInfo';

import { navigate } from './navigateService';

export type TypeRequestHeaders = Partial<
  Record<string, AxiosHeaderValue> & {
    /** API 버전  */
    version?: string;
    /** 파일 다운로드 여부 */
    isFileDownload?: boolean;
    /** 토큰 없이 요청 여부 */
    withoutToken?: boolean;
    /** 비밀번호 토큰 없이 요청 여부 */
    checkPassToken?: boolean;
    /** 개발 편의를 위한 기능인지 여부 */
    isMock?: boolean;
  }
>;

type CustomAxiosRequestConfig = InternalAxiosRequestConfig & {
  headers?: TypeRequestHeaders;
};

const tokenRefreshEndpoint = '/labor-accounts/token/refresh';

type PendingRequest = {
  retry: (accessToken: string) => Promise<AxiosResponse>;
  resolve: (value: AxiosResponse) => void;
  reject: (reason?: unknown) => void;
};

let pendingAPIList: PendingRequest[] = [];

const tokenExpiredError = new AxiosError('Unauthorized token', '401', undefined, undefined, {
  status: 401,
} as AxiosResponse);

let refreshPromise: Promise<string> | null = null;

export const axiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/${API_VERSION}`,
  timeout: REQUEST_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    Accept: `application/json`,
  },
});

axiosInstance.interceptors.request.use(async (config: CustomAxiosRequestConfig) => {
  config.baseURL = `${API_BASE_URL}${config.headers?.isMock ? '/qa' : ''}/${config.headers?.version ? config.headers.version : API_VERSION}`;

  if (config.headers?.withoutToken) {
    delete config.headers['Authorization'];
  } else {
    const accessToken = useTokenStore.getState().accessToken;
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  if (config.headers?.isFileDownload) {
    config.responseType = 'blob';
  }

  if (config.headers?.checkPassToken) {
    const passToken = useCheckPasswordStore.getState().passToken ?? '';
    config.headers.KurlyroPrivacyPassToken = passToken;
  }

  const isLocalNetworkPath = networkConfig.includes(window.location.pathname);
  const isSkipIpCheck = useSkipFeaturesStore.getState().isSkipIpCheck;

  if (isLocalNetworkPath && isSkipIpCheck) {
    config.headers['Restrict-Access-Api'] = 'PASS';
  }

  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { refreshToken, accessToken } = useTokenStore.getState();
    const response = error?.response;
    const originalRequest = error.config as CustomAxiosRequestConfig;

    // 이미 만료 처리된 토큰 에러는 그대로 전달
    if (error === tokenExpiredError) {
      return Promise.reject(error);
    }

    if (response?.status === 401 && accessToken) {
      // refreshToken이 없거나 토큰 갱신 실패 시, 인증 만료 모달 표시
      if (!refreshToken || originalRequest.url === tokenRefreshEndpoint || originalRequest.headers?.withoutToken) {
        showUnauthorizedTokenModal();
        return Promise.reject(error);
      }

      // 이미 토큰 갱신이 진행 중이면 해당 Promise 재사용
      if (!refreshPromise) {
        const currentStoredRefreshToken = useTokenStore.getState().refreshToken;

        refreshPromise = axiosInstance
          .post(tokenRefreshEndpoint, { refreshToken: currentStoredRefreshToken }, { headers: { withoutToken: true } })
          .then((refreshTokenResult) => {
            const { accessToken: newAccessToken, refreshToken: newRefreshToken } = refreshTokenResult.data.data;
            useTokenStore.getState().actions.setAccessToken(newAccessToken);
            useTokenStore.getState().actions.setRefreshToken(newRefreshToken);

            return newAccessToken as string;
          })
          .catch((error) => {
            console.error('[토큰 갱신 실패]', error);
            showUnauthorizedTokenModal();
            throw tokenExpiredError;
          })
          .finally(() => {
            refreshPromise = null;
          });
      }

      // 토큰 갱신 완료 후, 원 요청을 새로운 토큰으로 재요청
      return refreshPromise.then((newAccessToken) => {
        const originalHeaders = (originalRequest.headers || {}) as AxiosRequestHeaders;
        const headers = originalHeaders;

        if (headers.withoutToken) {
          delete headers.withoutToken;
        } else {
          headers.Authorization = `Bearer ${newAccessToken}`;
        }

        const retriedConfig: CustomAxiosRequestConfig = {
          ...originalRequest,
          headers,
        };

        return axiosInstance(retriedConfig);
      });
    }

    return Promise.reject(error);
  }
);

const showUnauthorizedTokenModal = () => {
  useLoadingStore.getState().actions.setLoading({ show: false, dimmed: true, zIndex: 99999 });

  // 모달 표시 전 모든 대기 요청을 reject 처리하여 원 호출부 onError로 전달
  if (pendingAPIList.length > 0) {
    pendingAPIList.forEach((item) => item.reject(tokenExpiredError));
  }

  pendingAPIList = [];
  dialogService.addDialog({
    type: 'alert',
    className: 'unauthorized-token-modal',
    title: DIALOG_TITLES.EXPIRED_TOKEN,
    children: DIALOG_MESSAGES.EXPIRED_TOKEN,
    onConfirm: async () => {
      useUserInfoStore.getState().actions.resetUserInfo();
      useTokenStore.getState().actions.resetTokens();
      useWorkInfoStore.getState().actions.resetWorkInfo();
      useContractInputStore.getState().actions.resetContractInputState();
      useTabInfoStore.getState().actions.resetTabInfo();
      useShouldCheckModalStore.getState().actions.resetChangePasswordModal();
    },
    onConfirmCompleted: () => {
      navigate('/login', { replace: true });
    },
  });
};
